/**
 * COOKIE LAYER
 * ============
 * When the native app (Android / iOS) opens this web page inside a WebView, it
 * first writes a handful of cookies onto the askk.co.in domain. Those cookies
 * are how the backend knows *which logged-in user* is asking for a horoscope.
 *
 * The native app sets exactly these six cookies (names are case-sensitive and
 * come straight from the Android PR — do not rename them):
 *
 *     userId, token, authSessionId, sessionId, deviceId, referrer
 *
 * This file is the ONLY place that knows how to read them. Everything else in
 * the app asks this file instead of touching `document.cookie` directly.
 */

/** The auth/session cookies the native app injects. */
export const AUTH_COOKIE_NAMES = [
  'userId',
  'token',
  'authSessionId',
  'sessionId',
  'deviceId',
  'referrer',
] as const

export type AuthCookieName = (typeof AUTH_COOKIE_NAMES)[number]

/**
 * Read a single cookie by name. Returns the decoded value, or `null` if the
 * cookie isn't set (e.g. when you open the page in a plain browser during
 * local development, where the native app never ran).
 *
 * Cookie names are case-sensitive.
 */
export function readCookie(name: string): string | null {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')
  const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'))
  if (!match) return null
  try {
    return decodeURIComponent(match[1])
  } catch {
    // Malformed %-escape — decodeURIComponent throws URIError. Fall back to the
    // raw value rather than breaking auth-header / analytics-envelope building.
    return match[1]
  }
}

/** All the auth cookies in one object. Any missing cookie comes back as null. */
export function getAuthContext(): Record<AuthCookieName, string | null> {
  return {
    userId: readCookie('userId'),
    token: readCookie('token'),
    authSessionId: readCookie('authSessionId'),
    sessionId: readCookie('sessionId'),
    deviceId: readCookie('deviceId'),
    referrer: readCookie('referrer'),
  }
}

/**
 * The version of the native app that opened this WebView. It is NOT hardcoded —
 * the app passes it in (different app builds report different versions). We read
 * an `appVersion` URL query param first, then an `appVersion` cookie.
 *
 * ⚠️ The native app must actually provide this (query param or cookie named
 * `appVersion`). Until it does, this returns undefined and the events envelope
 * simply omits the field rather than reporting a wrong version.
 */
export function getAppVersion(): number | undefined {
  let raw: string | null = null
  if (typeof window !== 'undefined' && window.location?.search) {
    raw = new URLSearchParams(window.location.search).get('appVersion')
  }
  if (!raw) raw = readCookie('appVersion')
  if (!raw) return undefined
  const n = Number(raw)
  return Number.isFinite(n) ? n : undefined
}

/**
 * Identity headers the backend uses to authenticate the user — the VALIDATABLE
 * set (userId + auth token + session/device), re-sent from the cookies the
 * native app dropped, using the same header names native interceptors use.
 *
 * The client does NOT send `X-SHARECHAT-AUTHORIZED-USERID` — that's the
 * gateway-verified header the API gateway injects ITSELF after validating these
 * headers. The client forging it would be spoofable, and it only "worked" before
 * because we were hitting preprod directly (bypassing the gateway). Hit the
 * gateway (apis.sharechat.com, or preprod via VPN) and it validates + injects.
 *
 * Scheme: V2 / accessToken — `token` is the auth token, no `secret`/passCode.
 * Used for both the feed (api/client.ts) and events (api/events.ts). The
 * `referrer` cookie stays in the event payload, NOT a header (matches native).
 */
export function getIdentityHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  const userId = readCookie('userId')
  const token = readCookie('token')
  const authSessionId = readCookie('authSessionId')
  const sessionId = readCookie('sessionId')
  const deviceId = readCookie('deviceId')
  if (userId) headers['X-SHARECHAT-USERID'] = userId
  if (token) headers['X-SHARECHAT-AUTH-TOKEN'] = token
  if (authSessionId) headers['X-SHARECHAT-AUTH-SESSION-ID'] = authSessionId
  if (sessionId) headers['SESSION-ID'] = sessionId
  if (deviceId) headers['DEVICE-ID'] = deviceId
  return headers
}
